import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import es from "@/messages/es.json";
import en from "@/messages/en.json";

export type Locale = "es" | "en";
type Messages = typeof es;
type MessageValue = string | Record<string, unknown>;
type Values = Record<string, string | number>;
type RichValues = Record<string, (chunks: ReactNode) => ReactNode>;

// Overrides de config aplanados por locale, con clave = ruta completa (namespace + key),
// p. ej. "deck.comercial.slides.portada.title". Los provee el overlay runtime.
export type MessageOverrides = Partial<Record<Locale, Record<string, string>>>;
// Reescritura de namespace para decks duplicados, p. ej. "deck.acme" -> "deck.comercial".
export type NamespaceAliases = Record<string, string>;
// Registro de los mensajes ya resueltos (ruta completa -> valor), solo en el preview
// editable: permite saber a qué clave corresponde un texto que se ve en pantalla.
export type MessageTrace = Map<string, string>;

interface I18nContextValue {
  locale: Locale;
  messages: Messages;
  overrides: MessageOverrides;
  aliases: NamespaceAliases;
  trace: MessageTrace | null;
  applyEdit: (path: string, value: string) => void;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function isLocale(value: string | undefined): value is Locale {
  return value === "es" || value === "en";
}

function getMessage(messages: Messages, namespace: string, key: string): MessageValue {
  const path = [...namespace.split("."), ...key.split(".")];
  let value: unknown = messages;
  for (const segment of path) {
    if (!value || typeof value !== "object") {
      return key;
    }
    value = (value as Record<string, unknown>)[segment];
  }
  return typeof value === "string" ? value : key;
}

/**
 * Resuelve una clave a su string final. Orden de lookup:
 *   1. override (ruta completa `namespace.key` en el locale)
 *   2. compilado, con el namespace reescrito por alias si corresponde (duplicados)
 *   3. la key cruda como último fallback (comportamiento de getMessage)
 * Pura y sin React: es el núcleo testeable de la i18n.
 */
export function resolveMessage(
  messages: Messages,
  overrides: MessageOverrides,
  aliases: NamespaceAliases,
  locale: Locale,
  namespace: string,
  key: string,
): string {
  const override = overrides[locale]?.[`${namespace}.${key}`];
  if (typeof override === "string") {
    return override;
  }
  const resolvedNamespace = aliases[namespace] ?? namespace;
  const value = getMessage(messages, resolvedNamespace, key);
  return typeof value === "string" ? value : key;
}

function interpolate(value: string, values?: Values): string {
  if (!values) return value;
  return value.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in values ? String(values[key]) : match,
  );
}

function richMessage(
  value: string,
  values: RichValues,
): ReactNode {
  const parts = value.split(/(<b>.*?<\/b>)/g);
  return parts.map((part, index) => {
    const match = part.match(/^<b>(.*?)<\/b>$/);
    if (!match) return part;
    return (
      <span key={index}>
        {values.b?.(match[1]) ?? match[1]}
      </span>
    );
  });
}

export interface TranslationFunction {
  (key: string, values?: Values): string;
  rich: (key: string, values: RichValues) => ReactNode;
}

/**
 * `untraced` deja los mensajes fuera del registro del preview editable. Lo usa el visor
 * para los nombres de slide, que resuelve para la navegación y no pinta en la slide.
 */
export function useTranslations(
  namespace: string,
  options?: { untraced?: boolean },
): TranslationFunction {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useTranslations must be used inside I18nProvider");
  }
  const trace = options?.untraced ? null : context.trace;

  const translate = ((key: string, values?: Values) => {
    const value = resolveMessage(
      context.messages,
      context.overrides,
      context.aliases,
      context.locale,
      namespace,
      key,
    );
    // Con interpolación el texto en pantalla ya no es el mensaje: no se puede editar ahí.
    if (trace && !values) trace.set(`${namespace}.${key}`, value);
    return interpolate(value, values);
  }) as TranslationFunction;

  translate.rich = (key, values) => {
    const value = resolveMessage(
      context.messages,
      context.overrides,
      context.aliases,
      context.locale,
      namespace,
      key,
    );
    return richMessage(value, values);
  };

  return translate;
}

export function useLocale(): Locale {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useLocale must be used inside I18nProvider");
  }
  return context.locale;
}

export function useMessageTrace(): MessageTrace | null {
  return useContext(I18nContext)?.trace ?? null;
}

/** Aplica en vivo una edición hecha sobre el preview (ruta completa; "" vuelve al compilado). */
export function useApplyMessageEdit(): (path: string, value: string) => void {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useApplyMessageEdit must be used inside I18nProvider");
  }
  return context.applyEdit;
}

const NO_OVERRIDES: MessageOverrides = {};
const NO_ALIASES: NamespaceAliases = {};

export function I18nProvider({
  locale,
  overrides = NO_OVERRIDES,
  aliases = NO_ALIASES,
  traceMessages = false,
  children,
}: {
  locale: Locale;
  overrides?: MessageOverrides;
  aliases?: NamespaceAliases;
  /** registra los mensajes resueltos para el preview editable (ver usePreviewTextEditing). */
  traceMessages?: boolean;
  children: ReactNode;
}) {
  const trace = useMemo<MessageTrace | null>(
    () => (traceMessages ? new Map() : null),
    [traceMessages],
  );
  // Ediciones hechas sobre el preview: quien edita las guarda en el borrador, pero este
  // visor no vuelve a pedir la config, así que las aplica encima para no mostrar el texto
  // viejo al re-renderizar la slide.
  const [edits, setEdits] = useState<Record<string, string>>({});
  const applyEdit = useCallback(
    (path: string, value: string) => setEdits((current) => ({ ...current, [path]: value })),
    [],
  );
  const effectiveOverrides = useMemo(() => {
    if (Object.keys(edits).length === 0) return overrides;
    const merged = { ...(overrides[locale] ?? {}) };
    for (const [path, value] of Object.entries(edits)) {
      if (value === "") delete merged[path];
      else merged[path] = value;
    }
    return { ...overrides, [locale]: merged };
  }, [overrides, edits, locale]);

  // es.json es la fuente de verdad del tipo. en.json puede no cubrir todos los decks
  // (p. ej. prospeccion es solo-es); sus claves ausentes usan el fallback de getMessage,
  // y las rutas EN de esos decks redirigen a es (ver App.tsx).
  const messages: Messages = locale === "en" ? (en as Messages) : es;
  return (
    <I18nContext.Provider
      value={{ locale, messages, overrides: effectiveOverrides, aliases, trace, applyEdit }}
    >
      {children}
    </I18nContext.Provider>
  );
}
